import React, { useMemo } from 'react';
import { rootId, TreeItem } from 'shared/components/ui/virtualized-tree-list';
import { FilterDocument } from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { overdueSectionID } from 'shared/types/orderable-section';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task } from 'shared/types/task';
import { DueDisplayOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import { NoGroupingTaskListRenderer } from '../no-grouping-tasks-list';

const noDisplayDue: DueDisplayOptions[] = [];

export type SingleDeadlineGroupTaskListProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  weekStartsOn: WeekDays;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  filterDocument?: FilterDocument;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
};

export const SingleDeadlineGroupTaskList: React.FC<
  SingleDeadlineGroupTaskListProps
> = ({
  treeData,
  tasks,
  onReorder,
  collapsedSections,
  onSectionCollapse,
  scrollElement,
  denseFormat,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
  showRepeatingAsTodo,
}) => {
  const { dateId, processedTreeData } = useMemo(() => {
    // process the tree to have all tasks directly on `Root` as children.
    const dateId = treeData[rootId].children?.[0];
    // also make sure the date is saved for reordering purposes
    const processedTreeData = {
      ...treeData,
      [rootId]: {
        ...treeData[rootId],
        children: Object.keys(treeData).filter(
          (id) => id !== rootId && id !== dateId,
        ),
      },
    };
    return { dateId, processedTreeData };
  }, [treeData]);

  const _onReorder = (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTasks: Task[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => {
    if (!onReorder || !dateId) {
      return;
    }
    onReorder(
      [{ id: dateId, taskSorting: updatedSections[0]?.taskSorting }],
      updatedTasks,
      relationshipUpdates,
    );
  };

  return (
    <NoGroupingTaskListRenderer
      treeData={processedTreeData}
      tasks={tasks}
      onReorder={_onReorder}
      collapsedSections={collapsedSections}
      onSectionCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      denseFormat={denseFormat}
      filterDocument={filterDocument}
      isContextMenuOpen={isContextMenuOpen}
      defaultNewTaskDate={defaultNewTaskDate}
      goalId={goalId}
      lifeAreaId={lifeAreaId}
      tagId={tagId}
      displayDue={dateId === overdueSectionID ? displayDue : noDisplayDue}
      showRepeatingAsTodo={showRepeatingAsTodo}
    />
  );
};
